import React from 'react'
import { Tooltip, PopoverInteractionKind } from '@blueprintjs/core'
import Select from 'react-select'
import Client from '../../../../client'
import { LoadableSelect } from '../../../../components/Inputs'
import NullSafeNumericInput from '../../../../components/NullSafeNumericInput'
import AuctionDurations from '../../../../constants/shopgoodwill/AuctionDurations'
import { SGW_BID_INCREMENT_LIMIT } from '../../../products_v5/components/JoinedForm/ShopgoodwillCard/constants'

const auctionOptions = new Array(16).fill(null).map((_, i) => ({
  name: i === 0 ? 'Immediately' : `${i} day${i === 1 ? '' : 's'} from now`,
  value: i
}))

interface SimpleNumericInputProps {
  onChange: (newValue: string) => void
  value: number | string | null
  placeholder?: string
  disabled?: boolean
}

export function StartingBid(props: SimpleNumericInputProps) {
  return (
    <label className="bp3-label">
      Starting Bid
      <NullSafeNumericInput
        width={70}
        min={0.01}
        placeholder={props.placeholder}
        clampValueOnBlur
        minorStepSize={0.01}
        selectAllOnFocus
        onValueChange={(_, s) => props.onChange(s)}
        value={props.value}
        disabled={props.disabled}
      />
    </label>
  )
}

export function BidIncrement(props: SimpleNumericInputProps) {
  return (
    <label className="bp3-label">
      Bid Increment
      <NullSafeNumericInput
        max={SGW_BID_INCREMENT_LIMIT}
        min={1}
        minorStepSize={0.01}
        placeholder={props.placeholder}
        clampValueOnBlur
        selectAllOnFocus
        onValueChange={(_, s) => props.onChange(s)}
        value={props.value}
        disabled={props.disabled}
      />
    </label>
  )
}
export function ReservePrice(props: SimpleNumericInputProps) {
  return (
    <label className="bp3-label">
      Reserve Price
      <NullSafeNumericInput
        min={0}
        clampValueOnBlur
        selectAllOnFocus
        placeholder={props.placeholder}
        minorStepSize={0.01}
        onValueChange={(_, s) => props.onChange(s)}
        value={props.value}
        disabled={props.disabled}
      />
    </label>
  )
}

export function StockQuantity(props: SimpleNumericInputProps) {
  return (
    <label className="bp3-label">
      Stock Quantity
      <NullSafeNumericInput
        min={0}
        clampValueOnBlur
        selectAllOnFocus
        placeholder={props.placeholder}
        minorStepSize={1}
        disabled={props.disabled}
        onValueChange={(_, s) => props.onChange(s)}
        value={props.value}
      />
    </label>
  )
}

export function BuyNowPrice(props: SimpleNumericInputProps) {
  return (
    <label className="bp3-label">
      Buy Now Price
      <NullSafeNumericInput
        min={0}
        placeholder={props.placeholder}
        disabled={props.disabled}
        clampValueOnBlur
        selectAllOnFocus
        minorStepSize={0.01}
        onValueChange={(_, s) => props.onChange(s)}
        value={props.value}
      />
    </label>
  )
}

export function HandlingPrice(props: SimpleNumericInputProps) {
  return (
    <label className="bp3-label">
      Handling Price
      <NullSafeNumericInput
        min={0}
        disabled={props.disabled}
        placeholder={props.placeholder}
        minorStepSize={0.01}
        clampValueOnBlur
        selectAllOnFocus
        onValueChange={(_, s) => props.onChange(s)}
        value={props.value}
      />
    </label>
  )
}

type ShippingPriceProps = SimpleNumericInputProps & { allowNull?: boolean }
export function ShippingPrice(props: ShippingPriceProps) {
  return (
    <label className="bp3-label">
      Shipping Price
      <NullSafeNumericInput
        min={0}
        minorStepSize={0.01}
        placeholder={props.placeholder}
        disabled={props.disabled}
        clampValueOnBlur
        selectAllOnFocus
        onValueChange={(_, s) => props.onChange(s)}
        value={props.allowNull ? props.value : props.value || 0}
      />
    </label>
  )
}
type AuctionStartProps = SimpleNumericInputProps & { clearable?: boolean }
export function AuctionStart(props: AuctionStartProps) {
  return (
    <label className="bp3-label">
      Auction Start
      <div style={{ maxWidth: '197px' }}>
        <Select
          value={props.value}
          disabled={props.disabled}
          onChange={v => props.onChange(v)}
          labelKey="name"
          valueKey="value"
          clearable={props.clearable || false}
          simpleValue
          placeholder={props.placeholder || 'Select...'}
          options={auctionOptions}
        />
      </div>
    </label>
  )
}

interface AuctionDurationProps {
  value: number
  onChange: (newDuration?: number) => void
  buyNowPrice?: number
  disabled?: boolean
  clearable?: boolean
  placeholder?: string
  isListed?: boolean
}

export function AuctionDuration(props: AuctionDurationProps) {
  let options = AuctionDurations.map(a => ({ value: a, name: `${a} day${a === 1 ? '' : 's'}` }))
  let { value } = props
  // can only go up to 15 days if an auction (no buy it now)
  if (props.buyNowPrice <= 0 || props.buyNowPrice === undefined) {
    options = options.map(o => {
      const isDisabled = o.value > 15
      return {
        ...o,
        disabled: isDisabled,
        name: isDisabled ? `${o.name} (Buy-it-now only)` : o.name
      }
    })

    if (value > 15) value = null
  }

  return (
    <label className="bp3-label">
      Auction Duration
      <div style={{ maxWidth: '197px' }}>
        <Tooltip
          interactionKind={PopoverInteractionKind.HOVER}
          disabled={!props.isListed}
          content="Auction duration for Shopgoodwill cannot be changed once the listing is live."
        >
          <Select
            value={value}
            disabled={props.disabled || props.isListed}
            onChange={v => props.onChange(v)}
            labelKey="name"
            valueKey="value"
            clearable={props.clearable || false}
            placeholder={props.placeholder || 'Select...'}
            simpleValue
            options={options}
            style={{ width: '197px' }}
          />
        </Tooltip>
      </div>
    </label>
  )
}

export function ShippingBox(props) {
  return (
    <LoadableSelect
      load={Client.shippingBoxIndex}
      disabled={props.disabled}
      process={d =>
        d.shippingBoxes.map(s => ({
          label: s.name,
          value: s.id
        }))
      }
      onChange={v => props.onChange(v)}
      {...props}
    />
  )
}

export function ListingStrategies(props) {
  return (
    <LoadableSelect
      load={Client.getListingStrategies}
      disabled={props.disabled}
      autoload
      process={d =>
        d.listingStrategies.map(l => ({
          label: l.name,
          value: l.id
        }))
      }
      onChange={v => props.onChange(v)}
      {...props}
    />
  )
}

export function Suppliers(props) {
  return (
    <LoadableSelect
      load={Client.suppliersIndex}
      disabled={props.disabled}
      process={d => d.suppliers.map(s => ({ value: s.id, label: s.name }))}
      onChange={v => props.onChange(v)}
      {...props}
    />
  )
}

export function InventoryLocation(props) {
  return (
    <LoadableSelect
      load={Client.inventoryLocationIndex}
      disabled={props.disabled}
      process={d => d.inventoryLocations.map(s => ({ value: s.id, label: s.name }))}
      onChange={v => props.onChange(v)}
      {...props}
    />
  )
}
